import type { AppPrompt, PromptAnalyticsRow } from './promptsV2Types';
import {
  hasPromptDescription,
  hasPromptDocument,
  hasPromptVideo,
  promptCompleteness,
  promptStatusLabel,
} from './promptsV2Utils';

export type PromptFilterTab = 'all' | 'complete' | 'needs-work';
export type PromptSortKey = 'order' | 'recent' | 'views';

export type PromptBreakdown = {
  prompts: { current: number; max: number };
  videos: { current: number; max: number };
  documents: { current: number; max: number };
  notes: { current: number; max: number };
};

export type RecommendedAction = {
  id: string;
  label: string;
  detail: string;
};

export function isPromptComplete(prompt: AppPrompt) {
  return promptCompleteness(prompt) >= 2;
}

export function isPromptNeedsWork(prompt: AppPrompt) {
  return !isPromptComplete(prompt);
}

export function formatRelativePromptDate(iso?: string) {
  if (!iso) return 'Recently updated';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Recently updated';

  const diffMs = Date.now() - date.getTime();
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days <= 0) return 'Updated today';
  if (days === 1) return 'Updated 1 day ago';
  if (days < 7) return `Updated ${days} days ago`;
  if (days < 14) return 'Updated 1 week ago';
  if (days < 30) return `Updated ${Math.floor(days / 7)} weeks ago`;
  if (days < 60) return 'Updated 1 month ago';
  return `Updated ${Math.floor(days / 30)} months ago`;
}

export function promptPublishedLabel(prompt: AppPrompt) {
  const created = prompt.created_at;
  const updated = prompt.updated_at;
  const useCreated =
    created && (!updated || new Date(created).getTime() >= new Date(updated).getTime() - 60000);
  if (useCreated) {
    const date = new Date(created!);
    if (Number.isNaN(date.getTime())) return formatRelativePromptDate(updated);
    const days = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (days <= 0) return 'Published today';
    if (days === 1) return 'Published 1 day ago';
    if (days < 7) return `Published ${days} days ago`;
    if (days < 14) return 'Published 1 week ago';
    return formatRelativePromptDate(updated);
  }
  return formatRelativePromptDate(updated);
}

export function computePromptBreakdown(prompts: AppPrompt[], maxPrompts = 6): PromptBreakdown {
  return {
    prompts: { current: prompts.length, max: maxPrompts },
    videos: {
      current: prompts.filter((p) => hasPromptVideo(p)).length,
      max: maxPrompts,
    },
    documents: {
      current: prompts.filter((p) => hasPromptDocument(p)).length,
      max: maxPrompts,
    },
    notes: {
      current: prompts.filter((p) => hasPromptDescription(p)).length,
      max: maxPrompts,
    },
  };
}

export function countPromptsThisWeek(prompts: AppPrompt[]) {
  const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  return prompts.filter((p) => {
    const created = new Date(p.created_at || 0).getTime();
    return Number.isFinite(created) && created >= weekAgo;
  }).length;
}

export function formatTrendHint(count: number) {
  if (count <= 0) return null;
  return `+${count} this week`;
}

export function computePromptHealthScore(prompts: AppPrompt[]) {
  if (prompts.length === 0) return 0;
  const slots = 6;
  const slotScore = (prompts.length / slots) * 35;
  const completeness =
    prompts.reduce((sum, p) => sum + promptCompleteness(p), 0) / (prompts.length * 2);
  const completenessScore = completeness * 65;
  return Math.round(Math.min(100, slotScore + completenessScore));
}

export function healthLabel(score: number) {
  if (score >= 85) return 'Excellent';
  if (score >= 70) return 'Strong';
  if (score >= 50) return 'Good';
  if (score >= 30) return 'Fair';
  return 'Needs work';
}

export function computeRankLabel(score: number, allScores: number[]): string | null {
  if (allScores.length < 2 || score <= 0) return null;
  const sorted = [...allScores].sort((a, b) => b - a);
  const rankIndex = sorted.findIndex((s) => s === score);
  if (rankIndex < 0) return null;
  const percentile = Math.round(((sorted.length - rankIndex) / sorted.length) * 100);
  if (percentile >= 90) return 'Top 10%';
  if (percentile >= 75) return 'Top 15%';
  if (percentile >= 50) return 'Top 50%';
  return null;
}

export function promptDisplayOrder(a: AppPrompt, b: AppPrompt) {
  const aId = a.id ?? 0;
  const bId = b.id ?? 0;
  if (aId !== bId) return aId - bId;
  const aTime = new Date(a.created_at || 0).getTime();
  const bTime = new Date(b.created_at || 0).getTime();
  return aTime - bTime;
}

export function buildPromptNumberMap(prompts: AppPrompt[]) {
  const map = new Map<number, number>();
  [...prompts].sort(promptDisplayOrder).forEach((prompt, index) => {
    map.set(prompt.id, index + 1);
  });
  return map;
}

export function filterAndSortPrompts(
  prompts: AppPrompt[],
  analyticsById: Map<number, PromptAnalyticsRow>,
  options: {
    tab: PromptFilterTab;
    sort: PromptSortKey;
    search: string;
  }
) {
  const query = options.search.trim().toLowerCase();
  let list = [...prompts];

  if (options.tab === 'complete') {
    list = list.filter(isPromptComplete);
  } else if (options.tab === 'needs-work') {
    list = list.filter(isPromptNeedsWork);
  }

  if (query) {
    list = list.filter((p) => {
      const haystack = `${p.content} ${p.description ?? ''}`.toLowerCase();
      return haystack.includes(query);
    });
  }

  list.sort((a, b) => {
    if (options.sort === 'views') {
      const av = analyticsById.get(a.id)?.views ?? 0;
      const bv = analyticsById.get(b.id)?.views ?? 0;
      if (bv !== av) return bv - av;
      return promptDisplayOrder(a, b);
    }
    if (options.sort === 'recent') {
      const aTime = new Date(a.updated_at || a.created_at || 0).getTime();
      const bTime = new Date(b.updated_at || b.created_at || 0).getTime();
      if (bTime !== aTime) return bTime - aTime;
      return promptDisplayOrder(a, b);
    }
    return promptDisplayOrder(a, b);
  });

  return list;
}

export function buildRecommendedActions(prompts: AppPrompt[], maxPrompts = 6): RecommendedAction[] {
  const actions: RecommendedAction[] = [];

  if (prompts.length < maxPrompts) {
    actions.push({
      id: 'add-prompt',
      label: 'Add another prompt',
      detail: `${maxPrompts - prompts.length} slot${maxPrompts - prompts.length === 1 ? '' : 's'} remaining`,
    });
  }

  const missingVideo = prompts.filter((p) => !hasPromptVideo(p)).length;
  if (missingVideo > 0) {
    actions.push({
      id: 'add-video',
      label: 'Record missing videos',
      detail: `${missingVideo} prompt${missingVideo === 1 ? '' : 's'} without video`,
    });
  }

  const missingDoc = prompts.filter((p) => !hasPromptDocument(p)).length;
  if (missingDoc > 0) {
    actions.push({
      id: 'add-doc',
      label: 'Upload supporting documents',
      detail: `${missingDoc} prompt${missingDoc === 1 ? '' : 's'} missing documents`,
    });
  }

  const missingNotes = prompts.filter((p) => !hasPromptDescription(p)).length;
  if (missingNotes > 0) {
    actions.push({
      id: 'add-notes',
      label: 'Add written context',
      detail: `${missingNotes} prompt${missingNotes === 1 ? '' : 's'} need notes`,
    });
  }

  if (actions.length === 0) {
    actions.push({
      id: 'share',
      label: 'Share your profile',
      detail: 'Your prompts look complete — share with recruiters',
    });
  }

  return actions.slice(0, 4);
}

export function statusBadgeClass(prompt: AppPrompt) {
  const label = promptStatusLabel(prompt);
  if (label === 'Complete') return 'sp-status sp-status--complete';
  if (label === 'Partial') return 'sp-status sp-status--polish';
  return 'sp-status sp-status--incomplete';
}

export function sumPromptViews(analytics: PromptAnalyticsRow[]) {
  return analytics.reduce((sum, row) => sum + (row.views ?? 0), 0);
}

export function formatCompactCount(value: number) {
  if (value >= 1000) {
    const k = value / 1000;
    return k >= 10 ? `${Math.round(k)}K` : `${k.toFixed(1).replace(/\.0$/, '')}K`;
  }
  return String(value);
}

export function promptDateMeta(prompt: AppPrompt) {
  const created = prompt.created_at;
  const updated = prompt.updated_at;
  const usePublished =
    created && (!updated || new Date(created).getTime() >= new Date(updated).getTime() - 60000);

  const relative = (iso?: string) => {
    if (!iso) return 'recently';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return 'recently';
    const days = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (days <= 0) return 'today';
    if (days === 1) return '1 day ago';
    if (days < 7) return `${days} days ago`;
    if (days < 14) return '1 week ago';
    if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
    if (days < 60) return '1 month ago';
    return `${Math.floor(days / 30)} months ago`;
  };

  if (usePublished) {
    return { prefix: 'Published', when: relative(created) };
  }
  return { prefix: 'Updated', when: relative(updated || created) };
}

export function promptTags(prompt: AppPrompt) {
  const tags: string[] = [];
  if (hasPromptVideo(prompt)) tags.push('Video');
  if (hasPromptDocument(prompt)) tags.push('Document');
  if (hasPromptDescription(prompt)) tags.push('Notes');
  return tags;
}

export function promptPerformanceRank(prompt: AppPrompt, score: number, allScores: number[]) {
  if (!isPromptComplete(prompt)) {
    return {
      rank: 'Needs Polish',
      rankLabel: 'Add more detail',
      rankTone: 'amber' as const,
    };
  }
  const rank = computeRankLabel(score, allScores);
  if (!rank) {
    return {
      rank: '—',
      rankLabel: 'Your rank',
      rankTone: 'green' as const,
    };
  }
  return { rank, rankLabel: 'Your rank', rankTone: 'green' as const };
}

export function filterTabLabel(tab: PromptFilterTab) {
  if (tab === 'all') return 'All';
  if (tab === 'complete') return 'Complete';
  return 'Needs Work';
}

export function filterTabFromLabel(label: string): PromptFilterTab {
  if (label === 'Complete') return 'complete';
  if (label === 'Needs Work') return 'needs-work';
  return 'all';
}

export const PORTFOLIO_PRO_TIPS = [
  'Prompts with videos get up to 3× more recruiter views.',
  'Add a supporting document when recruiters should review a resume, deck, or portfolio.',
  'A short description gives context before recruiters press play on your video.',
  'Fill all six prompt slots to maximize how recruiters discover you on Skip Screen.',
  'Complete prompts rank higher in prompt health and appear more polished to recruiters.',
];
